import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  TextField
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Videocam as VideocamIcon
} from '@mui/icons-material';

function toDatetimeLocalValue(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BAUpload = ({ onUpload, initialBaTestTime = null }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [baTestTime, setBaTestTime] = useState(() =>
    toDatetimeLocalValue(initialBaTestTime ? new Date(initialBaTestTime) : new Date())
  );
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!baTestTime) {
      setError('Please enter BA test time before uploading');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const parsed = new Date(baTestTime);
    if (Number.isNaN(parsed.getTime())) {
      setError('Please enter a valid BA test time');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }

    // Validate file size (max 25MB)
    if (file.size > 25 * 1024 * 1024) {
      setError('File size must be less than 25MB');
      return;
    }

    setError(null);
    uploadFile(file, parsed.toISOString());

    // Clear the file input to prevent persistence issues
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file, baTestTimeIso) => {
    try {
      setUploading(true);
      setProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await onUpload(file, baTestTimeIso);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 1000);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.message || 'Upload failed. Please try again.');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect({ target: { files } });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        Breath Analyzer Video Upload
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter the BA test time, then upload the video recording
      </Typography>

      <TextField
        label="BA Test Time"
        type="datetime-local"
        value={baTestTime}
        onChange={(e) => setBaTestTime(e.target.value)}
        required
        fullWidth
        disabled={uploading}
        InputLabelProps={{ shrink: true }}
        helperText="Time the breath analyzer test was performed"
        sx={{ mb: 2, mt: 1, textAlign: 'left' }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 4,
          mb: 2,
          cursor: uploading ? 'default' : 'pointer',
          '&:hover': uploading
            ? undefined
            : {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
        }}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <VideocamIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />

        <Typography variant="body1" gutterBottom>
          {uploading ? 'Uploading...' : 'Click to select video or drag and drop'}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Supported formats: MP4, MOV, AVI (Max 25MB)
        </Typography>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              {progress}% uploaded
            </Typography>
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Button
        variant="contained"
        startIcon={<CloudUploadIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || !baTestTime}
        sx={{ mt: 1 }}
      >
        {uploading ? 'Uploading...' : 'Select Video File'}
      </Button>
    </Box>
  );
};

export default BAUpload;
